import { Component, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { VersionControl } from '../../../core/services/version-control';

/** Where the banner sits, which decides its wording. */
export type PendingReviewsScope = 'dashboard' | 'page' | 'document';

/** The banner's main sentence for `count` pending reviews (count > 0). */
export function pendingReviewsHeadline(scope: PendingReviewsScope, count: number): string {
  if (scope === 'dashboard') {
    return count === 1 ? '1 version review is pending.' : `${count} version reviews are pending.`;
  }
  return scope === 'document'
    ? 'Latest changes are partly not applied for this document.'
    : 'Latest changes are partly not applied here.';
}

/** The sentence after the headline. */
export function pendingReviewsDetail(scope: PendingReviewsScope, count: number, refreshNote: boolean): string {
  if (scope === 'dashboard') {
    return 'Classification, roles, policies and graphs show the latest changes only after they are decided.';
  }
  const pending = count === 1 ? '1 review is pending.' : `${count} reviews are pending.`;
  return `${pending} Approve them to get the latest.${refreshNote ? ' These pages also update on their next refresh.' : ''}`;
}

/**
 * Tells the user that pages built from document text (classification, roles, policies, graphs)
 * lag behind while version reviews wait: dpe-backend re-enriches a document only once every
 * review touching it is decided. Renders nothing when no review is pending, or when the count
 * can't be fetched -- it only informs.
 */
@Component({
  selector: 'app-pending-reviews-banner',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './pending-reviews-banner.html',
  styleUrl: './pending-reviews-banner.css'
})
export class PendingReviewsBanner implements OnInit {
  @Input() scope: PendingReviewsScope = 'page';
  /** Count only the reviews touching this document (scope 'document'). */
  @Input() documentId: string | null = null;
  /** For pages rebuilt on their own runs (policy flows, inferences). */
  @Input() refreshNote = false;

  readonly pending = signal(0);

  constructor(private versionControl: VersionControl) {}

  ngOnInit(): void {
    // Fetched on every visit: the page is created again whenever the user comes back to it.
    this.versionControl.stats(this.documentId ?? undefined).subscribe({
      next: stats => this.pending.set(stats.pending),
      error: () => this.pending.set(0)
    });
  }

  headline(): string {
    return pendingReviewsHeadline(this.scope, this.pending());
  }

  detail(): string {
    return pendingReviewsDetail(this.scope, this.pending(), this.refreshNote);
  }
}
